import { Amount } from './Amount';
import { Account } from './Account';
import { areNumbersEqual, assertType } from "../base/utils";
import { Entry } from './Transaction';
import { DiffContent, Entity, Mutation, TrackingBuilder } from '../base';
import { uuid } from '../base';
import { CachingRef, Ref } from './Ref';


class TransactionTypeEntry {
    
    private _uuid: string;
    private _account: Ref<Account>;
    private _percentage: number;

    constructor(uuid:string, account:Ref<Account>, percentage:number) {
        assertType(uuid, "uuid", String);
        assertType(account, "account", Ref);
        assertType(percentage, "percentage", Number);

        this._uuid = uuid;
        this._account = account;
        this._percentage = percentage;
    }

    get uuid() { return this._uuid; }
    get account() { return this._account; }
    get percentage() { return this._percentage; }


    equals(entry:TransactionTypeEntry) : boolean {
        return this._account.uuid === entry.account.uuid
            && this._percentage === entry.percentage;
    }

    toJson() : TransactionTypeEntryJSON {
        return {
            uuid: this._uuid,
            account: this.account.uuid,
            percentage: this.percentage
        }
    }

    static fromJson(flowEntry : TransactionTypeEntryJSON) {
        const account = Account.createRef(flowEntry.account);
        return new TransactionTypeEntry(flowEntry.uuid, account, flowEntry.percentage);
    }
}

class TransactionType implements Entity<TransactionType> {
    static STORE = "TransactionType";

    private _uuid: string;
    private _name: string;
    private _entries: TransactionTypeEntry[];

    constructor(uuid : string, name:string, entries:TransactionTypeEntry[]) {
        assertType(uuid, "uuid", String);
        assertType(name, "name", String);
        assertType(entries, "entries", Array, TransactionTypeEntry);

        const sumPercent = entries.map(fe => fe.percentage)
            .reduce((a,b) => a + b, 0);

        if (!areNumbersEqual(sumPercent, 0)) {
            throw Error("FlowEntries percenateges should add to 0")
        }

        this._uuid = uuid;
        this._name = name;
        this._entries = entries;
    }

    get uuid() { return this._uuid; }
    get name() { return this._name; }
    get entries() { return this._entries; }

    getEntries( amount : Amount ) : Entry[] {
        return this._entries
            .map(fe => {
                const partAmount = TransactionType.calculatePartAmount(fe.percentage, amount);
                return new Entry(uuid(), fe.account, partAmount);
            });
    }

    builder() : TransactionTypeBuilder {
        return new TransactionTypeBuilder(this);
    }

    toJson() {
        return {
            uuid: this._uuid,
            name: this._name,
            entries: this._entries.map(fe => fe.toJson())
        }
    }

    getRef() {
        return CachingRef.of(TransactionType, this._uuid, this);
    }

    static identityValue = new TransactionType("", "", []);
    static identity() {
        return TransactionType.identityValue;
    }

    static createRef(uuid: string) {
        return CachingRef.of(TransactionType, uuid);
    }

    static calculatePartAmount(percentage:number, amount:Amount) : Amount {
        const part = (percentage * amount.value) / 100;
        const partRounded = Math.round(part * 100) / 100;
        return new Amount(partRounded, amount.currency);
    }

    static fromJson(flow:TransactionTypeJSON) {
        return new TransactionType(flow.uuid, flow.name, flow.entries.map(TransactionTypeEntry.fromJson));
    }    
}

class TransactionTypeBuilder extends TrackingBuilder<TransactionType> {

    private _uuid:string;
    private _name:string;
    private _entries:TransactionTypeEntry[];
    private _diff:TransactionTypeDiff = {};

    constructor(object?: TransactionType) {
        super();
        const value = object ? object : TransactionType.identity();
        this._uuid = value.uuid;
        this._name = value.name;
        this._entries = [...value.entries];
    }

    uuid(uuid:string) {
        if (this.isTracking() && this._uuid !== uuid) {
            this._diff.uuid = uuid;
        }
        this._uuid = uuid;
        return this;
    }

    name(name:string) {
        if (this.isTracking() && this._name !== name) {
            this._diff.name = name;
        }
        this._name = name;
        return this;
    }

    addFlowEntry(flowEntry:TransactionTypeEntry) {
        const contentIndex = this._entries.findIndex(f => f.equals(flowEntry));
        const uuidIndex = this._entries.findIndex(f => f.uuid === flowEntry.uuid);
        const isAdd = uuidIndex === -1 && contentIndex === -1;
        const isEdit = uuidIndex > -1 && contentIndex === -1;
        if (isEdit) {
            flowEntry = new TransactionTypeEntry(uuid(), flowEntry.account, flowEntry.percentage);
        }
        if (this.isTracking()) {
            if (isAdd || isEdit) {
                this._diff.newFlowEntries = this._diff.newFlowEntries || []
                this._diff.newFlowEntries.push(flowEntry.toJson());
            }
            if (isEdit) {
                this._diff.deletedFlowEntries = this._diff.deletedFlowEntries || [];
                this._diff.deletedFlowEntries.push(this._entries[uuidIndex].toJson());
            }
        }
        if (isAdd) {
            this._entries.push(flowEntry);
        }
        if (isEdit) {
            this._entries.splice(uuidIndex, 1);
        }

        return this;
    }

    removeFlowEntry(flowEntry: TransactionTypeEntry) {
        const index = this._entries.findIndex(f => f.uuid === flowEntry.uuid);
        if (this.isTracking() && index >= 0) {
            this._diff.deletedFlowEntries = this._diff.deletedFlowEntries || [];
            this._diff.deletedFlowEntries.push(flowEntry.toJson());
        }
        if (index >= 0) {
            this._entries.splice(index, 1);
        }
        return this;
    }

    addFlowEntries(entries:TransactionTypeEntry[]) {
        entries.forEach(this.addFlowEntry.bind(this));
        return this;
    }

    removeFlowEntries(entries:TransactionTypeEntry[]) {
        entries.forEach(this.removeFlowEntry.bind(this));
        return this;
    }

    addJsonFlowEntries(entries:TransactionTypeEntryJSON[]) {
        this.addFlowEntries(entries.map(TransactionTypeEntry.fromJson));
        return this;
    }

    removeJsonFlowEntries(entries:TransactionTypeEntryJSON[]) {
        this.removeFlowEntries(entries.map(TransactionTypeEntry.fromJson));
        return this;
    }

    build(): TransactionType {
        return new TransactionType(this._uuid, this._name, this._entries);
    }

    applyDiff(diff: TransactionTypeDiff): TransactionTypeBuilder {
        if (diff.uuid) {
            this.uuid(diff.uuid);
        }
        if (diff.name) {
            this.name(diff.name);
        }
        if (diff.newFlowEntries) {
            this.addJsonFlowEntries(diff.newFlowEntries);
        }
        if (diff.deletedFlowEntries) {
            this.removeJsonFlowEntries(diff.deletedFlowEntries);
        }
        return this;
    }

    applyObj(object: TransactionType) : TransactionTypeBuilder {
        this.uuid(object.uuid);
        this.name(object.name);

        const deletedEntries = [];
        for (let i = 0; i < this._entries.length; i++) {
            const flowEntry = this._entries[i];
            const found = object.entries.find(ofe => ofe.uuid === flowEntry.uuid);
            if (!found) {
                deletedEntries.push(flowEntry);
            }
        }
        this.removeFlowEntries(deletedEntries);
        this.addFlowEntries(object.entries);

        return this;
    }

    getDiff(): TransactionTypeDiff {
        return this._diff;
    }
}

interface TransactionTypeEntryJSON {
    uuid: string;
    account: string;
    percentage: number;
}

interface TransactionTypeJSON {
    uuid: string;
    name: string;
    entries: TransactionTypeEntryJSON[];
}

interface TransactionTypeDiff extends DiffContent<TransactionType> {
    uuid?: string;
    name?: string;
    newFlowEntries?: TransactionTypeEntryJSON[];
    deletedFlowEntries?: TransactionTypeEntryJSON[];
}


export {TransactionType, 
    TransactionTypeEntry,
    TransactionTypeEntryJSON,
    TransactionTypeJSON,
    TransactionTypeDiff,
    TransactionTypeBuilder 
}